/** Helpers for Production Details (filters, readiness, positions). */

import { formatScheduleDate } from './orgLpHelpers';
import { countryNameFromIso2 } from './countries';

export const LP_READINESS_STATUSES = [
  { key: 'not_started', label: 'Not started' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'ready_for_review', label: 'Ready for review' },
  { key: 'in_review', label: 'In review' },
  { key: 'action_needed', label: 'Action needed' },
  { key: 'verified', label: 'Verified' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'decision', label: 'Decision' },
  { key: 'complete', label: 'Complete' },
];

const READINESS_META = Object.fromEntries(
  LP_READINESS_STATUSES.map((s) => [s.key, s]),
);

const CLEARED_READINESS = new Set(['complete', 'submitted', 'decision', 'verified']);

/** Mutually-exclusive Immigration Status buckets (must match backend lp_readiness_service). */
export const IMMIGRATION_BUCKETS = [
  { key: 'complete', label: 'Complete', cls: 's-done' },
  { key: 'active', label: 'Active', cls: 's-active' },
  { key: 'at_risk', label: 'At risk', cls: 's-risk' },
  { key: 'not_started', label: 'Not started', cls: 's-ns' },
  { key: 'not_registered', label: 'Not registered', cls: 's-nr' },
];

export function lpReadinessLabel(status) {
  return READINESS_META[status]?.label || (status ? status.replace(/_/g, ' ') : '-');
}

export function lpReadinessClass(status) {
  const map = {
    not_started: 'bg-slate-50 text-slate-600 border-slate-200',
    in_progress: 'bg-sky-50 text-sky-700 border-sky-200',
    ready_for_review: 'bg-violet-50 text-violet-700 border-violet-200',
    in_review: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    action_needed: 'bg-amber-50 text-amber-800 border-amber-200',
    verified: 'bg-teal-50 text-teal-700 border-teal-200',
    submitted: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    decision: 'bg-orange-50 text-orange-800 border-orange-200',
    complete: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return map[status] || map.not_started;
}

export function workflowStatusLabel(status) {
  if (status === 'dormant') return 'Dormant';
  if (status === 'closed') return 'Closed';
  return 'Active';
}

export function workflowStatusClass(status) {
  if (status === 'dormant') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (status === 'closed') return 'bg-slate-100 text-slate-600 border-slate-200';
  return 'bg-emerald-50 text-emerald-700 border-emerald-200';
}

/** True when this person should appear on Mobility → Immigration. */
export function needsImmigration(person) {
  if (!person) return false;
  // Show anyone who has created an account - no visa / procedure gate.
  return person.status === 'registered' || Boolean(person.registered_at);
}

/** True once the person's case is confirmed done (backend `immigration_bucket`, falls back to legacy readiness). */
export function isCleared(person) {
  if (person?.immigration_bucket) return person.immigration_bucket === 'complete';
  return CLEARED_READINESS.has(person?.readiness_status);
}

/** Progress note for immigration list rows, driven by the backend's mutually-exclusive bucket. */
export function immigrationProgressNote(person) {
  const bucket = person?.immigration_bucket;
  if (!bucket) {
    // Legacy fallback for cached responses predating `immigration_bucket`.
    if (isCleared(person)) return '';
    if (person?.status !== 'registered') return 'Invited';
    return lpReadinessLabel(person?.readiness_status);
  }
  if (bucket === 'complete') return '';
  if (bucket === 'not_registered') return 'Not registered';
  if (bucket === 'not_started') return 'Not started';
  if (bucket === 'at_risk') {
    if (person.readiness_status === 'action_needed') return 'Action needed';
    return lpReadinessLabel(person.readiness_status) || 'At risk';
  }
  return lpReadinessLabel(person.readiness_status);
}

const REVIEW_READINESS = new Set(['action_needed', 'ready_for_review', 'in_review']);

/**
 * Immigration status shown in the Mobility table.
 *
 * Collapses the backend bucket / readiness pair into the four-word vocabulary
 * used by the Mobility design, plus a `tone` the stylesheet colours.
 *
 * @param {object} person
 * @returns {{ label: string, tone: 'ok' | 'progress' | 'wait' | 'review' | 'muted' }}
 */
export function immigrationStatusCell(person) {
  const bucket = person?.immigration_bucket;
  const readiness = person?.readiness_status;

  if (bucket === 'complete' || isCleared(person)) return { label: 'Ready', tone: 'ok' };
  if (bucket === 'not_registered') return { label: 'Not registered', tone: 'muted' };
  if (bucket === 'at_risk') return { label: 'Needs review', tone: 'review' };
  if (bucket === 'not_started') return { label: 'Waiting on participant', tone: 'wait' };
  if (REVIEW_READINESS.has(readiness)) return { label: 'Needs review', tone: 'review' };
  if (readiness === 'not_started') return { label: 'Waiting on participant', tone: 'wait' };
  return { label: 'In progress', tone: 'progress' };
}

/** Immigration participants table status (reference: Registered / In Process / Complete). */
export function immigrationListStatus(person) {
  const bucket = person?.immigration_bucket;
  if (bucket === 'complete' || isCleared(person)) {
    return { label: 'Complete', tone: 'complete' };
  }
  if (bucket === 'not_registered' || person?.status === 'invited') {
    return { label: 'Not registered', tone: 'not-registered' };
  }
  if (bucket === 'active' || bucket === 'at_risk') {
    return { label: 'In Process', tone: 'in-process' };
  }
  if (person?.status === 'registered' || Boolean(person?.registered_at)) {
    return { label: 'Registered', tone: 'registered' };
  }
  return { label: 'Not registered', tone: 'not-registered' };
}

export function daysUntil(isoDate) {
  if (!isoDate) return null;
  const target = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / 86400000);
}

/** Role dropdown options for the Production roster (grouped by line tier). */
export function buildRosterRoleOptions(participants) {
  const above = new Set();
  const below = new Set();
  (participants || []).forEach((p) => {
    const role = String(p.job_title || '').trim();
    if (!role) return;
    if (p.position_tier === 'above_the_line') above.add(role);
    else below.add(role);
  });
  return {
    above: [...above].sort((a, b) => a.localeCompare(b)),
    below: [...below].sort((a, b) => a.localeCompare(b)),
  };
}

/** Production roster filters - matches production-details.html drawRoster(). */
export function filterRosterPeople(participants, filters = {}) {
  const { role = '', nationality = '', search = '' } = filters;
  const q = String(search || '').trim().toLowerCase();
  return (participants || []).filter((p) => {
    if (role && (p.job_title || '') !== role) return false;
    if (nationality && (p.country || '') !== nationality) return false;
    if (q && !String(p.name || '').toLowerCase().includes(q)) return false;
    return true;
  });
}

export function buildPositionFilterOptions(participants) {
  const above = new Set();
  const below = new Set();
  (participants || []).forEach((p) => {
    const g = p.position_group || p.job_title || 'Unassigned';
    if (p.position_tier === 'above_the_line') above.add(g);
    else if (p.position_tier === 'below_the_line') below.add(g);
    else below.add(g);
  });
  const opts = [
    { value: '', label: 'All roles' },
    { value: 'tier:above_the_line', label: 'Above the line (all)', group: 'Above the line' },
    { value: 'tier:below_the_line', label: 'Below the line (all)', group: 'Below the line' },
  ];
  [...above].sort().forEach((g) => opts.push({ value: `pos:${g}`, label: g, group: 'Above the line' }));
  [...below].sort().forEach((g) => opts.push({ value: `pos:${g}`, label: g, group: 'Below the line' }));
  return opts;
}

/** True when this person belongs in the Immigration "At risk" filter. */
export function isImmigrationAtRisk(person) {
  if (!person) return false;
  if (person.immigration_bucket === 'at_risk') return true;
  return Boolean(person.at_risk);
}

/**
 * Immigration list filters (by-country / by-person views).
 * @param {object} filters
 * @param {boolean} [filters.riskOnly]
 * @param {string} [filters.role] - position filter value from buildPositionFilterOptions
 * @param {string} [filters.search]
 * @param {string} [filters.location] - "COUNTRY" or "COUNTRY|Location"
 * @param {string} [filters.shootCountry] - production destination used for grouping
 */
export function filterImmigrationPeople(participants, filters = {}) {
  const {
    riskOnly = false,
    role = '',
    search = '',
    location = '',
    immigrationOnly = true,
  } = filters;
  const q = String(search || '').trim().toLowerCase();

  return (participants || []).filter((p) => {
    if (immigrationOnly && !needsImmigration(p)) return false;
    // Only people in the at_risk bucket (registered + in progress + deadline close).
    if (riskOnly && !isImmigrationAtRisk(p)) return false;
    if (role) {
      if (role.startsWith('tier:')) {
        if (p.position_tier !== role.slice(5)) return false;
      } else if (role.startsWith('pos:')) {
        const group = role.slice(4);
        if ((p.position_group || p.job_title) !== group) return false;
      } else if ((p.job_title || p.position_group) !== role) {
        return false;
      }
    }
    if (q) {
      const hay = `${p.name || ''} ${p.email || ''} ${p.job_title || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (location) {
      const [countryPart, locPart] = String(location).split('|');
      if (locPart) {
        if ((p.location || '') !== locPart) return false;
      } else if (countryPart) {
        const shoot = String(filters.shootCountry || '').toUpperCase();
        if (shoot && shoot !== String(countryPart).toUpperCase()) return false;
      }
    }
    return true;
  });
}

/** @deprecated Prefer filterImmigrationPeople for the themed immigration UI. */
export function filterParticipants(participants, filters) {
  const { nationality, country, urgency, position } = filters;
  return (participants || []).filter((p) => {
    if (nationality && (p.country || '').toUpperCase() !== nationality.toUpperCase()) return false;
    if (country && !(p.country || '').toUpperCase().includes(country.toUpperCase())) {
      if ((p.country || '').toUpperCase() !== country.toUpperCase()) return false;
    }
    if (urgency === 'at_risk' && !isImmigrationAtRisk(p)) return false;
    if (urgency === 'on_track' && isImmigrationAtRisk(p)) return false;
    if (position) {
      if (position.startsWith('tier:')) {
        const tier = position.slice(5);
        if (p.position_tier !== tier) return false;
      } else if (position.startsWith('pos:')) {
        const group = position.slice(4);
        if ((p.position_group || p.job_title) !== group) return false;
      }
    }
    return true;
  });
}

export function formatLocations(locations) {
  if (!locations?.length) return '-';
  return locations
    .map((row) => `${row.country}: ${(row.locations || []).join(', ') || '-'}`)
    .join(' · ');
}

export function formatMustFileBy(date) {
  if (!date) return '-';
  return formatScheduleDate(date);
}

/** Progress bar width for a production task row. */
export function taskProgressPct(task) {
  const total = Number(task?.total_count) || 0;
  if (!total) return 0;
  return Math.round((Number(task?.done_count) || 0) / total * 100);
}

/**
 * Build country groups for the immigration "By country" view from live production data.
 * Groups by shoot destination (procedure-agnostic); people list is the filtered immigration set.
 */
export function buildCountryGroups(productionData, people) {
  const dest = (productionData?.project?.destination_country || '').trim().toUpperCase();
  const locationRows = productionData?.locations || [];
  const list = people || [];
  if (!list.length) return [];

  const atRiskCount = list.filter((p) => p.immigration_bucket === 'at_risk').length;

  if (locationRows.length) {
    return locationRows.map((row) => {
      const code = (row.country || dest || 'Production').toUpperCase();
      return {
        country: code,
        countryName: countryNameFromIso2(code),
        locations: row.locations || [],
        startDate: productionData?.schedule_start_date || null,
        people: list,
        atRiskCount,
      };
    });
  }

  const code = dest || 'Production';
  return [{
    country: code,
    countryName: countryNameFromIso2(code),
    locations: [],
    startDate: productionData?.schedule_start_date || null,
    people: list,
    atRiskCount,
  }];
}

/**
 * Per-country overview cards (flag, deadline, progress, per-location breakdown).
 * Mirrors production-details.html's `.ccard` rendering, driven by live participants.
 */
export function buildOverviewCountryCards(productionData, participants) {
  const dest = (productionData?.project?.destination_country || '').trim().toUpperCase();
  const locationRows = productionData?.locations?.length
    ? productionData.locations
    : (dest ? [{ country: dest, locations: [] }] : []);
  const pool = (participants || []).filter(needsImmigration);
  if (!pool.length) return [];

  const earliestStart = (list) => {
    const dates = list.map((p) => p.schedule_start_date).filter(Boolean).sort();
    return dates[0] || productionData?.schedule_start_date || null;
  };

  return locationRows.map((row) => {
    const code = (row.country || dest || '').toUpperCase();
    const cleared = pool.filter((p) => p.immigration_bucket === 'complete').length;
    const atRiskCount = pool.filter((p) => p.immigration_bucket === 'at_risk').length;
    const pct = pool.length ? Math.round((cleared / pool.length) * 100) : 0;
    const locations = (row.locations || []).map((locName) => {
      const locPool = pool.filter((p) => p.location === locName);
      const locAtRisk = locPool.filter((p) => p.immigration_bucket === 'at_risk').length;
      return {
        name: locName,
        date: earliestStart(locPool),
        atRisk: locAtRisk,
      };
    });
    return {
      country: code,
      countryName: countryNameFromIso2(code),
      deadline: earliestStart(pool),
      pct,
      cleared,
      poolLength: pool.length,
      atRiskCount,
      locations,
    };
  });
}

export function buildLocationFilterOptions(productionData) {
  const rows = productionData?.locations || [];
  const opts = [{ value: '', label: 'All locations' }];
  rows.forEach((row) => {
    const code = row.country;
    opts.push({ value: code, label: `All ${code}`, group: code });
    (row.locations || []).forEach((loc) => {
      opts.push({ value: `${code}|${loc}`, label: loc, group: code });
    });
  });
  return opts;
}

/**
 * Overview Immigration status bar segments.
 *
 * Sourced from the backend's `immigration_bucket_summary` (mutually exclusive
 * per-person buckets - see `compute_immigration_bucket` in
 * `lp_readiness_service.py`) so segment widths always sum to the population.
 * Falls back to counting `participants[].immigration_bucket` client-side if
 * the aggregate summary wasn't provided (e.g. older cached response).
 */
export function buildOverviewSegments(participants, summary) {
  const all = participants || [];
  const pop = all.length || 1;

  const raw = summary || IMMIGRATION_BUCKETS.reduce((acc, b) => {
    acc[b.key] = all.filter((p) => p.immigration_bucket === b.key).length;
    return acc;
  }, {});

  const counts = IMMIGRATION_BUCKETS.reduce((acc, b) => {
    acc[b.key] = raw[b.key] || 0;
    return acc;
  }, {});

  const segments = IMMIGRATION_BUCKETS
    .map((b) => ({ key: b.key, cls: b.cls, label: b.label, n: counts[b.key] || 0 }))
    .filter((s) => s.n > 0);

  return {
    pop: all.length,
    segments,
    pct: (n) => (all.length ? Math.round((n / pop) * 100) : 0),
    atRiskPeople: all.filter((p) => p.immigration_bucket === 'at_risk'),
    counts,
  };
}

const READY_CASE_STATUSES = new Set(['completed', 'approved', 'archived']);
const SUBMITTED_CASE_STATUSES = new Set(['submitted', 'ready_to_submit']);
const UNDER_REVIEW_CASE_STATUSES = new Set(['in_review_govt', 'in_review', 'decision']);

/**
 * Overview "Immigration status" columns.
 *
 * Mutually exclusive in precedence order so the five counts always sum to the
 * participant population (no double counting).
 */
/** Reference overview: Complete / At risk / In progress / Not started + donut %. */
export function buildReferenceImmigrationSummary(participants, summary) {
  const all = participants || [];
  const total = all.length;
  const raw = summary || IMMIGRATION_BUCKETS.reduce((acc, b) => {
    acc[b.key] = all.filter((p) => p.immigration_bucket === b.key).length;
    return acc;
  }, {});

  const complete = raw.complete || 0;
  const atRisk = raw.at_risk || 0;
  // Overview "In progress" = anyone who registered and has not finished
  // (backend `active` + registered-with-zero-progress `not_started`).
  // "Not started" = still invited / not registered only.
  const inProgress = (raw.active || 0) + (raw.not_started || 0);
  const notStarted = raw.not_registered || 0;
  const completePct = total ? Math.round((complete / total) * 100) : 0;
  const ringPct = total ? Math.min(100, Math.round(((complete + inProgress + atRisk) / total) * 100)) : 0;

  return {
    total,
    complete,
    atRisk,
    inProgress,
    notStarted,
    completePct,
    ringPct,
    completeLabel: `${complete} of ${total} complete`,
  };
}

/** Reference overview attention strip (people need attention). */
export function buildReferenceAttentionSummary(participants) {
  const all = participants || [];
  const needAttention = all.filter(
    (p) => p.immigration_bucket === 'at_risk' || isImmigrationAtRisk(p),
  );
  const inProgress = all.filter(
    (p) => p.immigration_bucket === 'active' || p.immigration_bucket === 'not_started',
  ).length;
  const notStarted = all.filter(
    (p) => p.immigration_bucket === 'not_registered',
  ).length;
  const total = needAttention.length || atRiskFallbackCount(all, inProgress, notStarted);
  return { total, inProgress, notStarted };
}

function atRiskFallbackCount(all, inProgress, notStarted) {
  return Math.min(all.length, inProgress + notStarted);
}

/** Reference location card rows for primary shoot country. */
export function buildReferenceLocationSummary(productionData, participants, shootCountryName) {
  const cards = buildOverviewCountryCards(productionData, participants);
  const primary = cards[0] || {
    countryName: shootCountryName || 'Production',
    cleared: 0,
    poolLength: (participants || []).length,
    atRiskCount: 0,
    locations: [],
  };
  const locations = (primary.locations || []).map((loc) => ({
    name: loc.name,
    date: loc.date ? formatScheduleDate(loc.date) : '',
    status: loc.atRisk > 0 ? 'At risk' : 'On track',
    atRisk: loc.atRisk > 0,
  }));
  if (!locations.length && productionData?.locations?.length) {
    productionData.locations.forEach((row) => {
      (row.locations || []).forEach((name) => {
        locations.push({ name, date: '', status: 'On track', atRisk: false });
      });
    });
  }
  if (!locations.length) {
    const names = new Set();
    (participants || []).forEach((p) => {
      const loc = String(p.location || '').trim();
      if (loc) names.add(loc);
    });
    [...names].forEach((name) => {
      locations.push({ name, date: '', status: 'On track', atRisk: false });
    });
  }
  return {
    countryName: primary.countryName || shootCountryName || 'Production',
    cleared: primary.cleared || 0,
    total: primary.poolLength || (participants || []).length,
    atRiskCount: primary.atRiskCount || 0,
    locations,
  };
}

export function buildOverviewStatusColumns(participants) {
  const all = participants || [];
  const counts = {
    ready: 0,
    submitted: 0,
    under_review: 0,
    waiting: 0,
    not_started: 0,
  };

  all.forEach((p) => {
    const caseStatus = String(p.case_status || '').trim().toLowerCase();
    const registered = p.status === 'registered' || Boolean(p.registered_at);

    if (p.immigration_bucket === 'complete' || READY_CASE_STATUSES.has(caseStatus)) {
      counts.ready += 1;
    } else if (SUBMITTED_CASE_STATUSES.has(caseStatus)) {
      counts.submitted += 1;
    } else if (UNDER_REVIEW_CASE_STATUSES.has(caseStatus)) {
      counts.under_review += 1;
    } else if (registered && caseStatus && caseStatus !== 'not_started' && caseStatus !== 'new') {
      counts.waiting += 1;
    } else {
      counts.not_started += 1;
    }
  });

  return [
    { key: 'ready', label: 'Ready to go', count: counts.ready },
    { key: 'submitted', label: 'Submitted', count: counts.submitted },
    { key: 'under_review', label: 'Under review', count: counts.under_review },
    { key: 'waiting', label: 'Waiting on participant', count: counts.waiting },
    { key: 'not_started', label: 'Not started', count: counts.not_started },
  ];
}

/** Overview locations table rows: Location | Total | Ready | Remaining */
export function buildOverviewLocationTable(productionData, participants) {
  const pool = participants || [];
  const names = new Set();
  (productionData?.locations || []).forEach((row) => {
    (row.locations || []).forEach((loc) => {
      if (String(loc || '').trim()) names.add(String(loc).trim());
    });
  });
  pool.forEach((p) => {
    const loc = String(p.location || '').trim();
    if (loc) names.add(loc);
  });
  if (!names.size) return [];

  return [...names].sort((a, b) => a.localeCompare(b)).map((name) => {
    const locPool = pool.filter((p) => String(p.location || '').trim() === name);
    const total = locPool.length;
    const ready = locPool.filter((p) => p.immigration_bucket === 'complete').length;
    return { name, total, ready, remaining: Math.max(0, total - ready) };
  });
}

/**
 * Overview "At risk" panel rows.
 *
 * Fixed blocker categories so the panel keeps a stable shape; counts come from
 * live participants plus the production's open tasks.
 */
export function buildAtRiskReasonCounts(participants, openTasks) {
  const all = participants || [];

  const waiting = all.filter((p) => {
    if (isCleared(p) || p.immigration_bucket === 'not_registered') return false;
    const registered = p.status === 'registered' || Boolean(p.registered_at);
    return registered;
  }).length;

  const passport = all.filter((p) => {
    if (isCleared(p)) return false;
    const note = (immigrationProgressNote(p) || '').toLowerCase();
    return note.includes('passport') || p.readiness_status === 'action_needed';
  }).length;

  const socialTask = (openTasks || []).find(
    (t) => /social/i.test(String(t.title || '')),
  );
  const social = socialTask
    ? (socialTask.outstanding_count
      ?? Math.max(0, (socialTask.total_count || 0) - (socialTask.done_count || 0)))
    : 0;

  const notRegistered = all.filter(
    (p) => p.immigration_bucket === 'not_registered' || p.status === 'invited',
  ).length;

  return [
    { label: 'Waiting on participant', count: waiting },
    { label: 'Passport problem', count: passport },
    { label: 'Social media posts', count: social },
    { label: 'Not registered', count: notRegistered },
  ];
}

/**
 * Public app origin for customer signup links (dev or app).
 * Prefer the live page host; when running on localhost, derive from REACT_APP_API_URL.
 */
export function productionRegisterBaseUrl() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = String(window.location.origin).replace(/\/$/, '');
    if (!/localhost|127\.0\.0\.1/i.test(origin)) {
      return origin;
    }
  }
  const api = String(process.env.REACT_APP_API_URL || '').trim();
  if (api.startsWith('http')) {
    return api.replace(/\/api\/?$/i, '').replace(/\/$/, '');
  }
  return 'https://dev.globalmobility.law';
}

/**
 * Working customer magic link for invitation emails.
 * Prefer the API's `customer_signup_url`; otherwise build `/signup?org={ref}` on the
 * current environment host (dev.globalmobility.law or app.globalmobility.law).
 */
export function productionRegisterDisplayUrl(customerSignupRef, customerSignupUrl) {
  const fromApi = String(customerSignupUrl || '').trim();
  if (fromApi) return fromApi;
  const ref = String(customerSignupRef || '').trim();
  if (!ref) return '';
  return `${productionRegisterBaseUrl()}/signup?org=${encodeURIComponent(ref)}`;
}

/**
 * Global Mobility registration invitation email — matches
 * https://global-mobility-line-producer.yentien.chatgpt.site/participant-invitation-email
 *
 * Sent from noreply@globalmobility.law with the participant's personal registration link.
 */
export function invitationEmailTemplate({
  projectName,
  producerName,
  recipientName,
  signupUrl,
  productionCode: _productionCode,
} = {}) {
  const title = String(projectName || 'Production').trim() || 'Production';
  const inviter = String(producerName || '').trim() || 'Your production';
  const first = String(recipientName || '').trim().split(/\s+/)[0] || '';
  const hiLine = first ? `Hi ${first},` : 'Hi,';
  const registerUrl = String(signupUrl || '').trim() || `${productionRegisterBaseUrl()}/signup`;
  const signInUrl = `${productionRegisterBaseUrl()}/login`;
  const subject = `You're invited to join ${title}`;

  const bodyText = [
    hiLine,
    '',
    `Join ${title}`,
    '',
    `${inviter} has invited you to register with Global Mobility for ${title}.`,
    '',
    'Use your personal link to complete your registration and provide the information needed for your immigration work.',
    '',
    `Complete your registration: ${registerUrl}`,
    '',
    'This personal invitation link can only be used once and will expire.',
    '',
    `Already registered? Sign in: ${signInUrl}`,
    '',
    'If you did not expect this invitation, you can ignore this email.',
    '',
    'Global Mobility protects the information you provide for the production and its immigration work.',
  ].join('\n');

  const esc = (s) => String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const safeHi = esc(hiLine);
  const safeTitle = esc(title);
  const safeInviter = esc(inviter);
  const safeSubject = esc(subject);
  const safeRegisterUrl = esc(registerUrl);
  const safeSignInUrl = esc(signInUrl);

  const bodyHtml = `<!doctype html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <meta name="x-apple-disable-message-reformatting" />
    <title>${safeSubject}</title>
    <style>
      @media only screen and (max-width: 620px) {
        .email-shell { width: 100% !important; }
        .email-pad { padding-left: 24px !important; padding-right: 24px !important; }
        .email-title { font-size: 31px !important; line-height: 36px !important; }
      }
    </style>
  </head>
  <body style="margin:0;padding:0;background:#f2f1ed;color:#1f2430;font-family:Arial,Helvetica,sans-serif;-webkit-font-smoothing:antialiased;">
    <div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;">${safeInviter} invited you to register for ${safeTitle} on Global Mobility.</div>
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="width:100%;background:#f2f1ed;">
      <tr>
        <td align="center" style="padding:32px 14px;">
          <table class="email-shell" role="presentation" width="600" cellspacing="0" cellpadding="0" border="0" style="width:600px;max-width:600px;background:#ffffff;border:1px solid #deded8;border-collapse:separate;">
            <tr>
              <td style="background:#1f2430;padding:16px 32px;">
                <table role="presentation" cellspacing="0" cellpadding="0" border="0"><tr>
                  <td width="38" height="38" align="center" valign="middle" style="width:38px;height:38px;border:1px solid #777b85;border-radius:8px;color:#ffffff;font-size:12px;font-weight:700;font-family:Arial,Helvetica,sans-serif;">GM</td>
                  <td style="padding-left:12px;color:#ffffff;font-size:15px;font-weight:700;font-family:Arial,Helvetica,sans-serif;">Global Mobility</td>
                </tr></table>
              </td>
            </tr>
            <tr>
              <td class="email-pad" style="padding:38px 40px 36px;font-family:Arial,Helvetica,sans-serif;">
                <p style="margin:0 0 18px;color:#1f2430;font-size:14px;line-height:22px;">${safeHi}</p>
                <h1 class="email-title" style="margin:0 0 20px;color:#1f2430;font-family:Georgia,'Times New Roman',serif;font-size:36px;line-height:42px;font-weight:400;letter-spacing:-0.5px;">Join ${safeTitle}</h1>
                <p style="margin:0 0 18px;color:#4f535d;font-size:14px;line-height:23px;"><strong style="color:#1f2430;">${safeInviter}</strong> has invited you to register with Global Mobility for ${safeTitle}.</p>
                <p style="margin:0 0 26px;color:#4f535d;font-size:14px;line-height:23px;">Use your personal link to complete your registration and provide the information needed for your immigration work.</p>
                <table role="presentation" cellspacing="0" cellpadding="0" border="0"><tr>
                  <td class="button-cell" align="center" bgcolor="#6251d6" style="background:#6251d6;border-radius:6px;">
                    <a class="button-link" href="${safeRegisterUrl}" style="display:inline-block;padding:14px 22px;color:#ffffff;font-size:13px;line-height:18px;font-weight:700;text-decoration:none;font-family:Arial,Helvetica,sans-serif;">Complete your registration</a>
                  </td>
                </tr></table>
                <p style="margin:18px 0 0;color:#696d78;font-size:11px;line-height:18px;">This personal invitation link can only be used once and will expire.</p>
                <p style="margin:18px 0 0;color:#4f535d;font-size:12px;line-height:20px;">Already registered? <a href="${safeSignInUrl}" style="color:#1f2430;font-weight:700;">Sign in</a></p>
                <p style="margin:22px 0 0;color:#4f535d;font-size:12px;line-height:20px;">If you did not expect this invitation, you can ignore this email.</p>
              </td>
            </tr>
            <tr>
              <td class="email-pad" style="padding:18px 40px;border-top:1px solid #deded8;background:#f7f6f2;color:#696d78;font-size:10px;line-height:16px;font-family:Arial,Helvetica,sans-serif;">Global Mobility protects the information you provide for the production and its immigration work.</td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;

  return { subject, bodyHtml, bodyText };
}
